import React from "react";
import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { MaintenanceState } from "api/automationTypes";
import { BlockStandard } from "components/admin/BlockStandard";
import { PhotoUploadGrid } from "./PhotoUploadGrid";
import { DeleteCTA } from "components/MUIExtensions/DeleteCTA";

const CONSERVATION_STATES = Object.values(MaintenanceState);

export const EngineDetail = ({
  engineData,
  enginePos,
  onChange,
  onDelete,
}: EngineDetailProps) => {
  const handleFieldChange =
    (field: keyof EngineDetailItem) =>
    ({ target }) => {
      onChange({ ...engineData, [field]: target.value });
    };

  const handlePhotosChange = (photos: File[]) => {
    onChange({ ...engineData, pictures: photos });
  };

  return (
    <BlockStandard>
      <TextField
        fullWidth
        label="Tipo de sistema"
        value={engineData.engineSystemType || ""}
        onChange={handleFieldChange("engineSystemType")}
      />

      <Grid
        item
        container
        direction="row"
        justifyContent="space-between"
        gap={2}
      >
        <TextField
          label="Potência do motor (cv)"
          placeholder="Informe"
          value={engineData.nominalPower || ""}
          onChange={handleFieldChange("nominalPower")}
          sx={{ flex: 1 }}
        />

        <TextField
          label="Carregamento médio (%)"
          placeholder="Ex.: 1"
          value={engineData.averageLoad || ""}
          onChange={handleFieldChange("averageLoad")}
          sx={{ flex: 1 }}
        />
      </Grid>

      <Grid
        item
        container
        direction="row"
        justifyContent="space-between"
        gap={2}
      >
        <TextField
          label="Rendimento nominal (%)"
          placeholder="%"
          value={engineData.nominalEfficiency || ""}
          onChange={handleFieldChange("nominalEfficiency")}
          sx={{ flex: 1 }}
        />

        <TextField
          label="Fator de potência"
          placeholder="Informe"
          value={engineData.powerFactor || ""}
          onChange={handleFieldChange("powerFactor")}
          sx={{ flex: 1 }}
        />
      </Grid>

      <TextField
        fullWidth
        label="Rendimento no ponto de carregamento (%)"
        placeholder="%"
        value={engineData.efficiencyAtLoadPoint || ""}
        onChange={handleFieldChange("efficiencyAtLoadPoint")}
      />

      <Grid
        item
        container
        direction="row"
        justifyContent="space-between"
        gap={2}
      >
        <TextField
          label="Quantidade"
          placeholder="Informe"
          value={engineData.quantity || ""}
          onChange={handleFieldChange("quantity")}
          sx={{ flex: 1 }}
        />

        <FormControl sx={{ flex: 1 }}>
          <InputLabel id={`select-frequency-inverter-${enginePos}`}>
            Inversor de frequência
          </InputLabel>
          <Select
            labelId={`select-frequency-inverter-${enginePos}`}
            label="Inversor de frequência"
            value={engineData.frequencyInverter || ""}
            onChange={handleFieldChange("frequencyInverter")}
          >
            <MenuItem value="">Selecione</MenuItem>
            <MenuItem value="Sim">Sim</MenuItem>
            <MenuItem value="Não">Não</MenuItem>
          </Select>
        </FormControl>
      </Grid>

      <Grid
        item
        container
        direction="row"
        justifyContent="space-between"
        gap={2}
      >
        <TextField
          label="Idade"
          placeholder="Informe"
          value={engineData.age || ""}
          onChange={handleFieldChange("age")}
          sx={{ flex: 1 }}
        />

        <FormControl sx={{ flex: 1 }}>
          <InputLabel id={`select-soft-starter-${enginePos}`}>
            Soft starter
          </InputLabel>
          <Select
            labelId={`select-soft-starter-${enginePos}`}
            label="Soft starter"
            value={engineData.softStarter || ""}
            onChange={handleFieldChange("softStarter")}
          >
            <MenuItem value="">Selecione</MenuItem>
            <MenuItem value="Sim">Sim</MenuItem>
            <MenuItem value="Não">Não</MenuItem>
          </Select>
        </FormControl>
      </Grid>

      <FormControl fullWidth>
        <InputLabel id={`select-conservation-${enginePos}`}>
          Estado de conservação
        </InputLabel>
        <Select
          labelId={`select-conservation-${enginePos}`}
          label="Estado de conservação"
          value={engineData.conservationStatus || ""}
          onChange={handleFieldChange("conservationStatus")}
        >
          {CONSERVATION_STATES.map((option) => (
            <MenuItem value={option} key={option}>
              {option}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        fullWidth
        multiline
        minRows={3}
        label="Observações"
        placeholder="Descreva sua observação"
        value={engineData.obs || ""}
        onChange={handleFieldChange("obs")}
      />

      <PhotoUploadGrid
        photos={engineData.pictures}
        onPhotosChange={handlePhotosChange}
        buttonText="Enviar foto"
      />

      {onDelete && <DeleteCTA onDeleteConfirmed={onDelete} />}
    </BlockStandard>
  );
};

export default EngineDetail;

export type EngineDetailItem = {
  engineSystemType: string; // free-text system name
  nominalPower: string; // Potência do motor (cv)
  averageLoad: string; // Carregamento médio (%)
  nominalEfficiency: string; // Rendimento nominal (%)
  powerFactor: string; // Fator de potência
  efficiencyAtLoadPoint: string; // Rendimento no ponto de carregamento (%)
  quantity: string;
  frequencyInverter: string; // Inversor de frequência (Sim/Não)
  age: string; // Idade
  softStarter: string; // Soft starter (Sim/Não)
  conservationStatus: MaintenanceState | "";
  obs: string; // Observações
  pictures: File[];
};

export const emptyEngine: EngineDetailItem = {
  engineSystemType: "",
  nominalPower: "",
  averageLoad: "",
  nominalEfficiency: "",
  powerFactor: "",
  efficiencyAtLoadPoint: "",
  quantity: "",
  frequencyInverter: "",
  age: "",
  softStarter: "",
  conservationStatus: "",
  obs: "",
  pictures: [],
};

export type EngineDetailProps = {
  engineData: EngineDetailItem;
  enginePos: number;
  onChange: (engine: EngineDetailItem) => void;
  onDelete?: () => void;
};
